'use client';
import { useState } from 'react';
import { X, PanelLeft, PanelRight, PanelTop, PanelBottom } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { ResizeHandle } from '../../components/ui/ResizeHandle';
import { Button } from '../../components/ui/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from '../../components/ui/shadcn/dropdown-menu';
import { BrainChat } from './BrainChat';
import type { UseDockState, DockSide } from '../../lib/useDockState';
export function AdvisorPanel({ dock, onClose, visible = true, presentation = 'dock' }: {
  dock: UseDockState;
  onClose: () => void;
  visible?: boolean;
  presentation?: 'dock' | 'studio-ask';
}) {
  const { t } = useTranslation();
  const { state, setSide, setWidth, setHeight, sizeRange, resetSize, finishResize } = dock;
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const horizontal = state.side === 'left' || state.side === 'right';
  const studioAsk = presentation === 'studio-ask';
  const headerIconSize = studioAsk ? 18 : 16;
  const iconStroke = studioAsk ? 1.5 : 2;
  const options: { side: DockSide; Icon: typeof PanelLeft; label: string }[] = [
    { side: 'left', Icon: PanelLeft, label: t.advisor.dockLeft }, { side: 'right', Icon: PanelRight, label: t.advisor.dockRight },
    { side: 'top', Icon: PanelTop, label: t.advisor.dockTop }, { side: 'bottom', Icon: PanelBottom, label: t.advisor.dockBottom },
  ];
  const ActiveIcon = options.find((option) => option.side === state.side)!.Icon;
  const panel = <div
    className={`advisor-panel ${presentation === 'studio-ask' ? 'advisor-panel--studio-ask' : ''} flex shrink-0 flex-col border-border bg-card ${horizontal ? 'h-full' : 'w-full'}`}
    data-presentation={presentation}
    data-visible={visible || undefined}
    aria-hidden={visible ? undefined : true}
    inert={visible ? undefined : true}
    style={horizontal ? { width: `min(${state.width}px, 100vw)` } : { height: `min(${state.height}px, 85dvh)` }}
  >
    <div className="advisor-panel__header flex items-center gap-1 border-b border-border px-2 py-1.5"><span className="text-section-title">{t.advisor.title}</span><div className="flex-1" />
      <DropdownMenu open={sideMenuOpen} onOpenChange={setSideMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="ghost" size="icon" aria-label={t.advisor.dockPosition} className="size-7 text-muted-foreground"><ActiveIcon size={headerIconSize} strokeWidth={iconStroke} aria-hidden /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" aria-label={t.advisor.dockPosition}>
          <DropdownMenuRadioGroup value={state.side} onValueChange={(side) => setSide(side as DockSide)}>
            {options.map(({ side, Icon, label }) => (
              <DropdownMenuRadioItem key={side} value={side}><Icon size={16} aria-hidden />{label}</DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label={t.advisor.close} className="size-7 text-muted-foreground"><X size={headerIconSize} strokeWidth={iconStroke} aria-hidden /></Button>
    </div>
    <div className="min-h-0 flex-1 overflow-hidden"><BrainChat /></div>
  </div>;
  const handle = <ResizeHandle
    orientation={horizontal ? 'vertical' : 'horizontal'}
    label={t.advisor.title}
    value={horizontal ? state.width : state.height}
    min={horizontal ? sizeRange.width.min : sizeRange.height.min}
    max={horizontal ? sizeRange.width.max : sizeRange.height.max}
    onDelta={(delta) => {
      const direction = state.side === 'right' || state.side === 'bottom' ? -1 : 1;
      if (horizontal) setWidth(state.width + direction * delta, false);
      else setHeight(state.height + direction * delta, false);
    }}
    onEnd={finishResize}
    onReset={resetSize}
    className={`advisor-panel__resize ${horizontal ? 'h-full' : ''}`}
  />;
  if (state.side === 'left' || state.side === 'top') return <>{panel}{handle}</>;
  return <>{handle}{panel}</>;
}
